/**
 * opSource — data-source transparency convention (2026-07-16 operator decision).
 *
 * Any layer serving a DECLARED-but-not-implemented op (CD preview seeds, temp
 * stubs) marks its JSON with `__source: "cd-preview-seed"`. Real backend /
 * real api-mocks responses never carry the marker. Call sites report every op
 * result here (see wf-api.ts `tag`); screens render <DataSourceStrip> naming
 * exactly which of their calls are not clean — a feature-by-feature to-do
 * signal for the backend team (REQ-034). When an op ships for real, its chip
 * disappears with zero UI changes.
 */
import { useEffect, useState } from "react";
import { DataSourceStrip as KitDataSourceStrip } from "@v0y4ge/jrni-ui";
import { useT } from "../lib/i18n";

export const SEED_MARK = "cd-preview-seed";

const sourceByOp = new Map<string, boolean>(); // op -> seeded?
const subs = new Set<() => void>();

export function reportOpSource(op: string, body: unknown): void {
  const isSeed = !!body && typeof body === "object" && (body as Record<string, unknown>).__source === SEED_MARK;
  if (sourceByOp.get(op) === isSeed) return;
  sourceByOp.set(op, isSeed);
  subs.forEach((f) => f());
}

/* `filter` is `readonly` so a caller can pass an `as const` op tuple (2026-09-09,
   CD TS4104): `booking-settings-data.ts` derives its `Op` union from
   `typeof BS_OPS[number]`, so the const is load-bearing there and the prop is the
   side that widens. Nothing in this chain mutates the list — it is read through
   `.includes` and every array returned is newly built — and a mutable array is
   assignable to a readonly one, so the other call sites are unaffected. */
export function seededOps(filter?: readonly string[]): string[] {
  const all = [...sourceByOp.entries()].filter(([, s]) => s).map(([op]) => op);
  const list = filter ? all.filter((op) => filter.includes(op)) : all;
  return list.sort();
}

export function useSeededOps(filter?: readonly string[]): string[] {
  const [, force] = useState(0);
  useEffect(() => {
    const f = () => force((n) => n + 1);
    subs.add(f);
    return () => { subs.delete(f); };
  }, []);
  return seededOps(filter);
}

/**
 * The convention's visual is now the design system's `DataSourceStrip` (adopted
 * 2026-08-19, §7) instead of this file's own dashed amber band. What stays here is
 * the part the design system cannot know: WHICH of a screen's ops are seeded, read
 * from the `__source` markers via `useSeededOps`.
 *
 * The visibility gate below is unchanged and is NOT this pass's to flip — see the
 * operator direction on it.
 */
export function DataSourceStrip({ screen, ops, reqId = "REQ-034" }: { screen: string; ops: readonly string[]; reqId?: string }) {
  const seeded = useSeededOps(ops);
  if (seeded.length === 0) return null;
  // Operator direction (2026-07-18): keep reportOpSource/useSeededOps tracking
  // wired (so it's ready the moment a real backend lands) but don't surface
  // the visual strip. Flip this back to `false` to re-show it.
  const HIDDEN = true;
  if (HIDDEN) return null;
  return (
    <KitDataSourceStrip
      source="seed"
      testid={`${screen}-seed-strip`}
      detail={`${seeded.join(", ")} · ${reqId}`}
      style={{ margin: "12px 24px 0" }}
    />
  );
}
